import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Grid, Card, CardContent, CircularProgress,
  Alert, Chip, LinearProgress, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Paper, Tabs, Tab,
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PieChartIcon from '@mui/icons-material/PieChart';
import PaymentsIcon from '@mui/icons-material/Payments';
import {
  getPortfolioSummary, getPortfolioInvestments,
  getPortfolioPerformance, getPortfolioBySector, getPortfolioDistributions,
} from '../services/api';

const formatCurrency = (v) => {
  if (v == null) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const formatPct = (v) => (v != null ? `${(v * 100).toFixed(1)}%` : '—');

const StatCard = ({ icon, label, value, color = '#478289' }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Box sx={{ color, mb: 1 }}>{icon}</Box>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
    </CardContent>
  </Card>
);

const PortfolioDashboard = () => {
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [investments, setInvestments] = useState([]);
  const [performance, setPerformance] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [distributions, setDistributions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [sumRes, invRes, perfRes, secRes, distRes] = await Promise.all([
          getPortfolioSummary(),
          getPortfolioInvestments(),
          getPortfolioPerformance(),
          getPortfolioBySector(),
          getPortfolioDistributions(),
        ]);
        setSummary(sumRes.data);
        setInvestments(invRes.data?.investments || invRes.data || []);
        setPerformance(perfRes.data);
        setSectors(secRes.data?.sectors || secRes.data || []);
        setDistributions(distRes.data?.distributions || distRes.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load portfolio');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #052d50 0%, #478289 100%)', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ fontWeight: 700 }}>My Portfolio</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, mt: 1 }}>Track your investments and performance</Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} md={3}>
            <StatCard icon={<AccountBalanceIcon fontSize="large" />} label="Total Invested" value={formatCurrency(summary?.totalInvested)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<TrendingUpIcon fontSize="large" />} label="Current Value" value={formatCurrency(summary?.currentValue)} color="#2e7d32" />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<PieChartIcon fontSize="large" />} label="IRR" value={formatPct(performance?.irr)} />
          </Grid>
          <Grid item xs={6} md={3}>
            <StatCard icon={<PaymentsIcon fontSize="large" />} label="TVPI" value={performance?.tvpi?.toFixed(2) + 'x' || '—'} />
          </Grid>
        </Grid>

        {/* Additional Metrics */}
        {performance && (
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={6} md={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#052d50' }}>{performance?.dpi?.toFixed(2) || '—'}x</Typography>
                <Typography variant="body2" color="textSecondary">DPI (Distributions to Paid-In)</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#052d50' }}>{summary?.activeCount || 0}</Typography>
                <Typography variant="body2" color="textSecondary">Active Investments</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} md={4}>
              <Card><CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#052d50' }}>{summary?.exitCount || 0}</Typography>
                <Typography variant="body2" color="textSecondary">Exits</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>
        )}

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}
            sx={{ '& .Mui-selected': { color: '#478289' }, '& .MuiTabs-indicator': { backgroundColor: '#478289' } }}>
            <Tab label="Investments" />
            <Tab label="Sector Breakdown" />
            <Tab label="Distributions" />
          </Tabs>
        </Box>

        {/* Investments Table */}
        {tab === 0 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current Value</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Multiple</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {investments.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{inv.startupName || inv.startup?.name || '—'}</TableCell>
                    <TableCell>{formatCurrency(inv.amount)}</TableCell>
                    <TableCell>{formatCurrency(inv.currentValue)}</TableCell>
                    <TableCell>{inv.multiple ? `${inv.multiple.toFixed(2)}x` : '—'}</TableCell>
                    <TableCell>
                      <Chip label={inv.status || 'active'} size="small"
                        color={inv.status === 'exited' ? 'success' : inv.status === 'written-off' ? 'error' : 'primary'} />
                    </TableCell>
                    <TableCell>{inv.date ? new Date(inv.date).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Sector Breakdown */}
        {tab === 1 && (
          <Grid container spacing={3}>
            {sectors.map((s, i) => (
              <Grid item xs={12} md={6} key={i}>
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{s.sector || s.name}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      {s.count} investments · {formatCurrency(s.totalValue || s.amount)}
                    </Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={s.percentage || 0}
                    sx={{ height: 10, borderRadius: 5, backgroundColor: '#e0e0e0',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#478289', borderRadius: 5 } }} />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Distributions */}
        {tab === 2 && (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {distributions.map((d, i) => (
                  <TableRow key={d.id || i} hover>
                    <TableCell>{d.startupName || d.startup?.name || '—'}</TableCell>
                    <TableCell>{formatCurrency(d.amount)}</TableCell>
                    <TableCell><Chip label={d.type || 'distribution'} size="small" /></TableCell>
                    <TableCell>{d.date ? new Date(d.date).toLocaleDateString() : '—'}</TableCell>
                  </TableRow>
                ))}
                {distributions.length === 0 && (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}>No distributions yet</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Container>
    </Box>
  );
};

export default PortfolioDashboard;
